import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, ChevronLeft, Loader2, Music, Sparkles, HeartHandshake, Send, Share2, CheckCircle2, PartyPopper } from 'lucide-react';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GenreHubPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { playTrack } = useAudioPlayer();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/genres/${slug}/hub`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) setHub(await res.json());
    } catch (e) {
      console.error('hub load error', e);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const play = (track, list, idx) => playTrack(track, list, idx);

  const [sharingCard, setSharingCard] = useState(false);
  const shareSpotlight = async () => {
    if (sharingCard) return;
    setSharingCard(true);
    try {
      const res = await fetch(`${API_URL}/api/genres/${slug}/spotlight-card`);
      if (!res.ok) throw new Error('card fetch failed');
      const blob = await res.blob();
      const fname = `nltradio_artist_of_the_week_${slug}.jpg`;
      const file = new File([blob], fname, { type: 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: 'Artist of the Week — NLT Radio' });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 4000);
    } catch (e) {
      console.error('spotlight share error', e);
    } finally {
      setSharingCard(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0910] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
      </div>
    );
  }
  if (!hub) {
    return (
      <div className="min-h-screen bg-[#0B0910] flex flex-col items-center justify-center text-white gap-4">
        <p>Genre not found.</p>
        <button onClick={() => navigate('/')} className="px-5 py-2 rounded-full bg-[#FF007F]">Back to Radio</button>
      </div>
    );
  }

  const TrackRow = ({ track, list, idx, testid }) => (
    <button
      data-testid={testid}
      onClick={() => play(track, list, idx)}
      className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-colors text-left group"
    >
      <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-white/10 flex-shrink-0">
        {track.artwork
          ? <img src={track.artwork} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Music className="w-5 h-5 text-white/40" /></div>}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
          <Play className="w-5 h-5 text-white" fill="white" />
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm font-semibold truncate">{track.track_title || 'Untitled'}</p>
        <p className="text-[#A1A1AA] text-xs truncate">{track.artist_name || 'Unknown Artist'}</p>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0B0910] text-white pb-32" data-testid="genre-hub-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/25 via-[#FF007F]/15 to-transparent" />
        <div className="relative px-5 pt-6 pb-8 max-w-3xl mx-auto">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-[#A1A1AA] text-sm mb-6" data-testid="hub-back-btn">
            <ChevronLeft className="w-4 h-4" /> Radio
          </button>
          <p className="uppercase tracking-[0.2em] text-xs text-[#FFD700] mb-2">Genre Station</p>
          <h1 className="text-4xl sm:text-5xl font-black leading-tight">{hub.label}</h1>
          <p className="text-lg text-white/80 mt-2 font-semibold">{hub.tagline}</p>
          <p className="text-[#A1A1AA] text-sm mt-3">{hub.track_count} track{hub.track_count === 1 ? '' : 's'} in this station</p>
        </div>
      </div>

      {/* Artist of the Week spotlight */}
      {hub.spotlight && (
        <div className="px-5 max-w-3xl mx-auto -mt-2 mb-8" data-testid="hub-spotlight">
          <div className="relative overflow-hidden rounded-2xl border border-[#FFD700]/25 bg-gradient-to-r from-[#FFD700]/10 via-[#FF007F]/10 to-transparent p-4 flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl overflow-hidden bg-white/10 flex-shrink-0 ring-2 ring-[#FFD700]/40">
              {hub.spotlight.artist_profile_picture || hub.spotlight.top_track?.artwork
                ? <img src={hub.spotlight.artist_profile_picture || hub.spotlight.top_track?.artwork} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-white/40 text-2xl font-bold">{(hub.spotlight.artist_name || '?')[0]}</div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[#FFD700] text-[11px] font-bold uppercase tracking-[0.15em]">
                <Sparkles className="w-3.5 h-3.5" /> Artist of the Week
              </div>
              <h3 className="text-white text-xl font-black truncate mt-0.5" data-testid="spotlight-name">{hub.spotlight.artist_name || 'Featured Artist'}</h3>
              <p className="text-[#A1A1AA] text-xs truncate">
                {hub.spotlight.track_count} track{hub.spotlight.track_count === 1 ? '' : 's'} · {hub.spotlight.plays} play{hub.spotlight.plays === 1 ? '' : 's'}
              </p>
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              {hub.spotlight.top_track && (
                <button
                  onClick={() => play(hub.spotlight.top_track, [hub.spotlight.top_track], 0)}
                  data-testid="spotlight-play-btn"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#FF007F] to-[#FFD700] text-white text-xs font-bold"
                >
                  <Play className="w-3.5 h-3.5" fill="white" /> Play
                </button>
              )}
              <button
                onClick={() => navigate(`/room/${hub.spotlight.artist_id}`)}
                data-testid="spotlight-room-btn"
                className="px-4 py-2 rounded-full border border-white/15 text-white/80 text-xs font-semibold hover:bg-white/5"
              >
                Visit room
              </button>
              <button
                onClick={shareSpotlight}
                disabled={sharingCard}
                data-testid="spotlight-share-btn"
                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-full border border-[#FFD700]/40 text-[#FFD700] text-xs font-semibold hover:bg-[#FFD700]/10 disabled:opacity-50"
              >
                {sharingCard ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />} Share story
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="px-5 max-w-3xl mx-auto space-y-10 mt-2">
        {hub.track_count === 0 && (
          <div className="text-center py-10 border border-white/10 rounded-2xl" data-testid="hub-empty">
            <p className="text-white font-semibold">No tracks here yet</p>
            <p className="text-[#A1A1AA] text-sm mt-1">Be the first — upload a {hub.label} release to light up this station.</p>
            <button onClick={() => navigate('/upload')} className="mt-4 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF007F] to-[#FFD700] text-white font-bold text-sm" data-testid="hub-upload-btn">
              Upload a track
            </button>
          </div>
        )}

        {/* New releases */}
        {hub.new_releases?.length > 0 && (
          <section data-testid="hub-new-releases">
            <h2 className="text-lg font-bold mb-3">New Releases</h2>
            <div className="space-y-1">
              {hub.new_releases.map((t, i) => (
                <TrackRow key={t.id || i} track={t} list={hub.new_releases} idx={i} testid={`hub-track-${t.id || i}`} />
              ))}
            </div>
          </section>
        )}

        {/* Artist grid */}
        {hub.artists?.length > 0 && (
          <section data-testid="hub-artists">
            <h2 className="text-lg font-bold mb-3">Artists</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {hub.artists.map((a, i) => (
                <button key={a.artist_id || i} onClick={() => navigate(`/room/${a.artist_id}`)} className="flex flex-col items-center gap-2" data-testid={`hub-artist-${a.artist_id || i}`}>
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-white/10">
                    {a.artist_profile_picture
                      ? <img src={a.artist_profile_picture} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-white/40 text-xl font-bold">{(a.artist_name || '?')[0]}</div>}
                  </div>
                  <span className="text-xs text-[#A1A1AA] text-center truncate w-full">{a.artist_name}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Curated playlists */}
        {hub.playlists?.filter(p => p.tracks?.length).map((pl) => (
          <section key={pl.id} data-testid={`hub-playlist-${pl.id}`}>
            <h2 className="text-lg font-bold">{pl.title}</h2>
            <p className="text-[#A1A1AA] text-xs mb-3">{pl.description}</p>
            <div className="space-y-1">
              {pl.tracks.map((t, i) => (
                <TrackRow key={t.id || i} track={t} list={pl.tracks} idx={i} testid={`hub-pl-track-${pl.id}-${i}`} />
              ))}
            </div>
          </section>
        ))}

        {/* Prayer Wall — Gospel Hip Hop community */}
        {slug === 'gospel-hip-hop' && <PrayerWall slug={slug} />}
      </div>
    </div>
  );
};

const PrayerWall = ({ slug }) => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [prayers, setPrayers] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [answerOpen, setAnswerOpen] = useState(null);
  const [testimony, setTestimony] = useState('');
  const [answering, setAnswering] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/genres/${slug}/prayers`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPrayers(data.prayers || []);
      }
    } catch (e) {
      console.error('prayers load error', e);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const t = text.trim();
    if (!t || posting) return;
    if (!isAuthenticated) { navigate('/auth'); return; }
    setPosting(true);
    try {
      const res = await fetch(`${API_URL}/api/genres/${slug}/prayers`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ text: t }),
      });
      if (res.ok) {
        const data = await res.json();
        setPrayers((prev) => [data.prayer, ...prev]);
        setText('');
      }
    } catch (e) {
      console.error('prayer post error', e);
    } finally {
      setPosting(false);
    }
  };

  const pray = async (id) => {
    if (!isAuthenticated) { navigate('/auth'); return; }
    setPrayers((prev) => prev.map((p) => p.id === id
      ? { ...p, my_prayed: !p.my_prayed, pray_count: p.pray_count + (p.my_prayed ? -1 : 1) }
      : p));
    try {
      const res = await fetch(`${API_URL}/api/genres/${slug}/prayers/${id}/pray`, { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPrayers((prev) => prev.map((p) => p.id === id ? { ...p, my_prayed: data.prayed, pray_count: data.pray_count } : p));
      }
    } catch (e) {
      console.error('pray error', e);
    }
  };

  const submitAnswered = async (id) => {
    if (answering) return;
    setAnswering(true);
    try {
      const res = await fetch(`${API_URL}/api/genres/${slug}/prayers/${id}/answered`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ testimony: testimony.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setPrayers((prev) => prev.map((p) => p.id === id
          ? { ...p, answered: true, answered_at: data.answered_at, testimony: data.testimony } : p));
        setAnswerOpen(null);
        setTestimony('');
      }
    } catch (e) {
      console.error('answered error', e);
    } finally {
      setAnswering(false);
    }
  };

  return (
    <section data-testid="hub-prayer-wall">
      <div className="flex items-center gap-2 mb-1">
        <HeartHandshake className="w-5 h-5 text-[#FFD700]" />
        <h2 className="text-lg font-bold">Prayer Wall</h2>
      </div>
      <p className="text-[#A1A1AA] text-xs mb-4">Share a request or a praise report. The Prayer Warrior community lifts each other up.</p>

      {/* Composer */}
      <div className="flex items-end gap-2 mb-5">
        <textarea
          data-testid="prayer-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => { if (!isAuthenticated) navigate('/auth'); }}
          placeholder={isAuthenticated ? 'What can the community pray for?' : 'Sign in to share a prayer request'}
          rows={2}
          maxLength={500}
          className="flex-1 resize-none rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FFD700]/50"
        />
        <button
          data-testid="prayer-submit-btn"
          onClick={submit}
          disabled={posting || !text.trim()}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#FFD700] text-white text-sm font-bold disabled:opacity-40"
        >
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-[#FFD700] animate-spin" /></div>
      ) : prayers.length === 0 ? (
        <div className="text-center py-8 border border-white/10 rounded-2xl" data-testid="prayer-empty">
          <p className="text-white/80 text-sm font-semibold">No prayers on the wall yet</p>
          <p className="text-[#A1A1AA] text-xs mt-1">Be the first to share a request.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {prayers.map((p) => (
            <div key={p.id} className={`rounded-2xl border p-3.5 ${p.answered ? 'bg-[#39FF14]/5 border-[#39FF14]/25' : 'bg-white/5 border-white/10'}`} data-testid={`prayer-${p.id}`}>
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                  {p.user_photo
                    ? <img src={p.user_photo} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-white/40 text-xs font-bold">{(p.user_name || '?')[0]}</div>}
                </div>
                <span className="text-white text-sm font-semibold truncate">{p.user_name}</span>
                {p.answered && (
                  <span className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#39FF14]/15 text-[#39FF14] text-[11px] font-bold" data-testid={`prayer-answered-badge-${p.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Answered
                  </span>
                )}
              </div>
              <p className="text-white/90 text-sm whitespace-pre-wrap break-words">{p.text}</p>

              {/* Praise report */}
              {p.answered && p.testimony && (
                <div className="mt-2.5 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/20 p-2.5 flex gap-2" data-testid={`prayer-testimony-${p.id}`}>
                  <PartyPopper className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  <p className="text-[#c9ffce] text-sm whitespace-pre-wrap break-words">{p.testimony}</p>
                </div>
              )}

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => pray(p.id)}
                  data-testid={`prayer-pray-btn-${p.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${p.my_prayed ? 'bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40' : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'}`}
                >
                  <HeartHandshake className="w-3.5 h-3.5" />
                  {p.my_prayed ? 'Prayed' : 'I prayed'} · {p.pray_count}
                </button>

                {/* Author-only: mark answered */}
                {isAuthenticated && user?.id === p.user_id && !p.answered && (
                  <button
                    onClick={() => { setAnswerOpen(answerOpen === p.id ? null : p.id); setTestimony(''); }}
                    data-testid={`prayer-mark-answered-${p.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/30 hover:bg-[#39FF14]/25 transition-colors"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Mark answered
                  </button>
                )}
              </div>

              {/* Answered composer */}
              {answerOpen === p.id && (
                <div className="mt-3 space-y-2" data-testid={`prayer-answer-composer-${p.id}`}>
                  <textarea
                    value={testimony}
                    onChange={(e) => setTestimony(e.target.value)}
                    placeholder="Share a praise report (optional) — what happened?"
                    rows={2}
                    maxLength={500}
                    data-testid={`prayer-testimony-input-${p.id}`}
                    className="w-full resize-none rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#39FF14]/50"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => submitAnswered(p.id)}
                      disabled={answering}
                      data-testid={`prayer-answer-submit-${p.id}`}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#39FF14] text-[#050505] text-sm font-bold disabled:opacity-50"
                    >
                      {answering ? <Loader2 className="w-4 h-4 animate-spin" /> : <PartyPopper className="w-4 h-4" />} Share the win
                    </button>
                    <button
                      onClick={() => { setAnswerOpen(null); setTestimony(''); }}
                      className="px-4 py-2 rounded-full border border-white/15 text-white/70 text-sm hover:bg-white/5"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default GenreHubPage;
